import React, { useEffect, useRef, useState } from "react"
import { RichText, withDataBox } from "../../../design-system/molecules/RichText"
import MaterialSvgDisplay from "./MaterialSvgDisplay"
import {IS_WEB} from "@common/utils/mobileUtils"
import {View} from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"

const FIGURE_IMAGE_MARK = "![figure]("

/**
 * Shared component for displaying material content with statement/SVG/body layout
 * Used in both MaterialDisplayModal and GenerateActivityModal
 *
 * When the material announces a figure (figure_description) that isn't in the
 * body yet, the figure PNG is generated server-side a few seconds after the
 * statement — so we poll the material until ![figure](url) shows up in its
 * text, then display the fresh version live. Legacy materials whose figure is
 * an SVG (figure_svg) keep the dedicated SVG display below the text.
 *
 * `reading` comes from the student's « Ma lecture » sheet (`EnonceReader`): the font, size and
 * adapted reading — which also puts the statement's data in a « À utiliser » box at the top.
 */
const MaterialContentDisplay = (props) => {
    const {
        material,
        showBackgroundGenerationMessage = false,
        style = {},
        width = "100%",
        reading,
    } = props

    const [freshMaterial, setFreshMaterial] = useState(null)
    const [figureTimedOut, setFigureTimedOut] = useState(false)
    const intervalRef = useRef()
    const timeoutRef = useRef()

    const materialId = material?.id
    const displayed = freshMaterial?.id === materialId ? freshMaterial : material
    const hasInlineFigure = !!displayed?.text?.includes(FIGURE_IMAGE_MARK)
    const awaitingFigure =
        !!materialId &&
        !!displayed?.figure_description &&
        !displayed?.figure_svg &&
        !hasInlineFigure

    useApiStatus(
        activitiesService.getMaterial,
        null,
        false,
        (data) => {
            if (data?.id !== materialId) return
            // Fresh figure arrived — inline PNG in the body, or legacy SVG
            if (data?.text?.includes(FIGURE_IMAGE_MARK) || data?.figure_svg) {
                setFreshMaterial(data)
                clearInterval(intervalRef.current)
                clearTimeout(timeoutRef.current)
            }
        },
        () => {
            // On error, keep polling until timeout
        },
        true // noIdle - don't clear the API state
    )

    useEffect(() => {
        setFreshMaterial(null)
        setFigureTimedOut(false)
        if (!awaitingFigure) return

        intervalRef.current = setInterval(() => {
            props.apiRequest(activitiesService.getMaterial, { id: materialId })
        }, 5000)
        timeoutRef.current = setTimeout(() => {
            setFigureTimedOut(true)
            clearInterval(intervalRef.current)
        }, 60000)

        return () => {
            clearInterval(intervalRef.current)
            clearTimeout(timeoutRef.current)
        }
    }, [materialId])

    if (!material) {
        return null
    }


    return (
        <View style={[(!IS_WEB ? { flexGrow: 1 }  : {}), style]}>

            {/* L'énoncé se lit dans le moteur du design system : mêmes rôles (consigne, donnée,
                notion, attention) et même structure que la fiche parent, sur web et natif. */}
            <RichText
                value={reading?.adapted ? withDataBox(displayed?.text ?? "") : (displayed?.text ?? "")}
                reading={reading}
                style={{ width }}
            />

            {/* Figure PNG being generated server-side — subtle wait indicator */}
            {awaitingFigure && !figureTimedOut && (
                <View
                    style={{
                        alignItems: "center",
                        justifyContent: "center",
                        paddingVertical: 15,
                        backgroundColor: theme.colors.lightGrey || theme.colors.grey + "10",
                        borderRadius: 8,
                        margin: 10,
                    }}
                >
                    <ActivityIndicator
                        color={theme.colors.primary}
                        size="small"
                        style={{ opacity: 0.7 }}
                    />
                    <TextM
                        style={{ marginTop: 6, textAlign: "center" }}
                        color={theme.colors.grey}
                        fontSize={11}
                    >
                        Génération de la figure...
                    </TextM>
                    {showBackgroundGenerationMessage && (
                        <TextM
                            style={{ marginTop: 4, textAlign: "center", paddingHorizontal: 10 }}
                            color={theme.colors.grey}
                            fontSize={8}
                            wrap
                        >
                            Tu peux créer l&apos;activité, la génération de l&apos;image continuera en arrière-plan
                        </TextM>
                    )}
                </View>
            )}

            {/* Legacy SVG figures keep their dedicated display below the text.
                !! matters: figure_svg="" rendered as an empty text node inside
                the View (react-native-web console error on every re-render). */}
            {!!(displayed?.figure_svg && displayed?.id) && (
                <MaterialSvgDisplay
                    material={displayed}
                    showBackgroundGenerationMessage={showBackgroundGenerationMessage}
                    height={(IS_WEB) ? 400 : 80}
                />
            )}

        </View>
    )
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(null, mapDispatchToProps)(MaterialContentDisplay)
